/**
 * pwaInstall.js — PWA install prompt + service worker registration
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 */
import { log } from '../utils/logger.js';
import { showToast } from '../utils/helpers.js'; // [10.19.32] честный тост вместо вырезанного диалога-сироты

let _swRegistration = null;

// Универсальная функция получения активного SW
function _getSW() {
  if (navigator.serviceWorker?.controller) return navigator.serviceWorker.controller;
  if (_swRegistration) return _swRegistration.active || _swRegistration.waiting || null;
  return null;
}

export function initPWA() {
  // [10.0.3] Разовый перенос очереди из localStorage в IndexedDB.
  _migrateLegacyQueue().catch(() => {});

  // ══════════════════════════════════════════════════════════════
  // INSTALL PROMPT STATE
  // ══════════════════════════════════════════════════════════════
  let _deferredInstallPrompt = null;
  let _pwaInstallDismissed = sessionStorage.getItem('pwa-install-dismissed') === '1';

  // [10.1.4] Предложение установить PWA убрано целиком: программа ставится
  // настольным установщиком, а веб-версия открывается по ссылке. Разметки
  // баннеров и кнопок больше нет, поэтому и слушать нечего.
  //
  // Регистрация служебного потока ниже ОСТАЁТСЯ — она про офлайн и кэш
  // веб-версии, а не про установку, и к этой правке отношения не имеет.
  if (false) window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredInstallPrompt = e;
    log.debug('[PWA] beforeinstallprompt fired');

    if (!_pwaInstallDismissed) {
      setTimeout(() => {
        document.getElementById('pwa-install-banner')?.classList.add('visible');
      }, 3000);
    }

    document.getElementById('btn-pwa-install')?.classList.add('visible');
    const mobileBtn = document.getElementById('mobile-pwa-install');
    if (mobileBtn) mobileBtn.style.display = '';
  });

  // ══════════════════════════════════════════════════════════════
  // INSTALL LOGIC
  // ══════════════════════════════════════════════════════════════
  const doInstall = async () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    if (isStandalone) {
      alert('✅ Приложение уже установлено и работает в PWA-режиме');
      return;
    }

    // If we have the deferred prompt — use it immediately
    if (_deferredInstallPrompt) {
      try {
        _deferredInstallPrompt.prompt();
        const { outcome } = await _deferredInstallPrompt.userChoice;
        log.debug(`[PWA] User choice: ${outcome}`);
        _deferredInstallPrompt = null;
        document.getElementById('pwa-install-banner')?.classList.remove('visible');
      } catch (e) { log.debug('[PWA] prompt error:', e.message); }
      return;
    }

    // No deferred prompt yet — wait briefly for it
    const gotPrompt = await new Promise((resolve) => {
      const handler = (e) => { e.preventDefault(); _deferredInstallPrompt = e; resolve(true); };
      window.addEventListener('beforeinstallprompt', handler, { once: true });
      setTimeout(() => { window.removeEventListener('beforeinstallprompt', handler); resolve(false); }, 1500);
    });

    if (gotPrompt && _deferredInstallPrompt) {
      _deferredInstallPrompt.prompt();
      const { outcome } = await _deferredInstallPrompt.userChoice;
      log.debug(`[PWA] User choice (delayed): ${outcome}`);
      _deferredInstallPrompt = null;
      return;
    }

    // [10.19.32] Ветка «показать окно с инструкцией по установке» ВЫРЕЗАНА
    // вместе со слушателями pwa-modal-overlay / pwa-btn-* / pwa-m-*: сам
    // диалог удалён из разметки ещё в 10.1.4 («кнопка установки PWA убрана»),
    // и весь блок годами дремал под null-проверками — сирота, невидимая
    // глазу и полезная только как место для будущей ошибки. Если браузер не
    // дал системного предложения установки — честно говорим об этом тостом.
    showToast('Установка недоступна: браузер не предложил её. Настольная версия ставится установщиком из папки выпуска', 'info', 5000);
    return;
  }

  // [10.19.32] Здесь же дремали слушатели pwa-btn-dismiss и pwa-install-banner
  // — баннера и кнопки нет в разметке с 10.1.4, обе строки были сиротами под
  // null-проверками. Вырезаны вместе с диалогом выше; живое в этом модуле —
  // служебный поток, очередь офлайна и «Очистить кэш» (btn-clear-cache).

  // Экспорт в window (оставлено для совместимости с инлайн-событиями в HTML)
  window._mobilePWAInstall = doInstall;

  // ══════════════════════════════════════════════════════════════
  // APP INSTALLED EVENT
  // ══════════════════════════════════════════════════════════════
  window.addEventListener('appinstalled', () => {
    log.debug('[PWA] App installed');
    _deferredInstallPrompt = null;
    // [10.19.32] строки с pwa-install-banner/btn-pwa-install убраны — элементов нет с 10.1.4
  });

  if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
    log.debug('[PWA] Running as installed app');
    document.body.classList.add('pwa-standalone');
    // [10.19.32] метка на mobile-pwa-install убрана — кнопки нет в разметке
  }

  // ══════════════════════════════════════════════════════════════
  // SERVICE WORKER REGISTRATION & UPDATE FLOW
  // ══════════════════════════════════════════════════════════════
  // [10.1.0] В настольной сборке служебный поток не нужен и вреден.
  //
  // Он там ничего не решает: файлы и так лежат на диске, сети нет, офлайн —
  // единственный режим. Зато он умеет отдать СТАРЫЙ бандл после обновления
  // программы — ровно та путаница, из-за которой в 6.7.37 пришлось
  // синхронизировать CACHE_VERSION со сборкой. Заголовки COOP/COEP, ради
  // которых он тоже держался, в настольной сборке ставит сама оболочка.
  // [10.19.18] В настольной СБОРКЕ служебного потока нет физически: плагин
  // VitePWA работает только для веба (см. vite.config.js), файла sw.js в
  // dist-desktop не существует. Проверять только window.MikhmelDesktop мало —
  // это признак ОБОЛОЧКИ, а сборку можно открыть и в браузере; тогда регистрация
  // уходила бы за несуществующим файлом и давала 404 в журнале на ровном месте.
  const _настольнаяСборка = (typeof __BUILD_MODE__ !== 'undefined') && __BUILD_MODE__ === 'desktop';
  if ('serviceWorker' in navigator && !window.MikhmelDesktop?.isDesktop && !_настольнаяСборка) {

    // Перезагрузка ТОЛЬКО когда пользователь подтвердил обновление SW
    // (НЕ при первой установке/активации — это вызывало reload после загрузки всех срезов)
    let _userConfirmedUpdate = false;
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (_userConfirmedUpdate && !refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });

    navigator.serviceWorker.ready.then((reg) => {
      _swRegistration = reg;
      log.debug('[PWA] SW ready, scope:', reg.scope);
      _getSW()?.postMessage('GET_CACHE_SIZE');
    }).catch(() => { /* SW not ready — non-critical */ });

    // [11.1.67] В РЕЖИМЕ РАЗРАБОТКИ СЛУЖЕБНЫЙ ПОТОК НЕ РЕГИСТРИРУЕМ.
    //
    // Регистрация шла безусловно, а плагин PWA собирает sw.js ТОЛЬКО в
    // веб-сборке (vite.config: РЕЖИМ === 'web'). В dev по адресу ./sw.js файла
    // нет, и dev-сервер отдаёт на него index.html — то есть браузер получает
    // HTML вместо служебного потока. В лучшем случае это ошибка в консоли, в
    // худшем поток встаёт и начинает перехватывать запросы, включая /models/ и
    // /ort-wasm/, которые в dev раздаёт наш плагин. Модели тогда не грузятся, а
    // причина выглядит как «ИИ не работает».
    //
    // ВНИМАНИЕ: выход только ВОКРУГ РЕГИСТРАЦИИ, а не из всей функции. Ниже в
    // ней ещё 111 строк: показ состояния «нет сети» и кнопка очистки кэша.
    // Ранний return обрубил бы и то и другое — в dev пропала бы полоска
    // офлайна. Первая моя правка была именно такой; поймано проверкой того,
    // что стоит после цепочки.
    //
    // import.meta.env.DEV даёт Vite: в сборке он ложь, в dev — истина.
    if (import.meta.env?.DEV) {
      log.info('[PWA] режим разработки: служебный поток не регистрируется — sw.js собирается только в веб-сборке');
    } else {
    navigator.serviceWorker.register('./sw.js').then((reg) => {
      _swRegistration = reg;
      log.debug('[PWA] SW registered');

      // Логика обновления Service Worker
      reg.addEventListener('updatefound', () => {
        const newSW = reg.installing;
        if (!newSW) return;
        
        newSW.addEventListener('statechange', () => {
          // Если SW установился и есть старый активный SW (это обновление, а не первая установка)
          if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
            if (confirm('Доступна новая версия приложения. Обновить сейчас?')) {
              _userConfirmedUpdate = true;
              // ВАЖНО: Отправляем команду новому SW активироваться
              newSW.postMessage({ type: 'SKIP_WAITING' });
            }
          }
        });
      });
    }).catch((err) => {
      log.debug('[PWA] SW registration skipped:', err.message);
    });
    }

    // Обработка сообщений от SW
    navigator.serviceWorker.addEventListener('message', (e) => {
      if (e.data?.type === 'CACHE_SIZE') {
        const sizeMB = (e.data.size / 1024 / 1024).toFixed(1);
        log.debug(`[PWA] Cache size: ${sizeMB} MB`);
        const sizeEl = document.getElementById('pwa-cache-size');
        if (sizeEl) sizeEl.textContent = `${sizeMB} МБ`;
      }
    });

    // ══════════════════════════════════════════════════════════════
    // ONLINE / OFFLINE STATUS
    // ══════════════════════════════════════════════════════════════
    const toggleOfflineUI = (isOffline) => {
      document.body.classList.toggle('offline-mode', isOffline);
      const badge = document.getElementById('offline-badge');
      if (badge) badge.style.display = isOffline ? 'flex' : 'none';
    };

    window.addEventListener('online', () => toggleOfflineUI(false));
    window.addEventListener('offline', () => toggleOfflineUI(true));
    
    if (!navigator.onLine) {
      toggleOfflineUI(true);
    }
  }

  // ══════════════════════════════════════════════════════════════
  // CLEAR CACHE LOGIC
  // ══════════════════════════════════════════════════════════════
  const btnCache = document.getElementById('btn-clear-cache');
  if (btnCache) {
    btnCache.addEventListener('click', async () => {
      let sw = _getSW();
      if (!sw && 'serviceWorker' in navigator) {
        try {
          const reg = await navigator.serviceWorker.ready;
          _swRegistration = reg;
          sw = _getSW();
        } catch (e) {
          log.error('Failed to get SW ready state', e);
        }
      }
      
      if (!sw) {
        alert('Service Worker не поддерживается или не активен.\nПерезагрузите страницу.');
        return;
      }
      _doClearCache(sw);
    });
  }

  function _doClearCache(sw) {
    if (!confirm('Очистить кэш PWA?\n\nOK — удалить весь кэш (потребуется интернет для повторной загрузки)')) return;
    
    const handler = (e) => {
      if (e.data === 'CACHES_CLEARED' || e.data?.type === 'CACHES_CLEARED') {
        navigator.serviceWorker.removeEventListener('message', handler);
        alert('Кэш очищен! Страница будет перезагружена.');
        location.reload();
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handler);
    sw.postMessage('CLEAR_ALL_CACHES');
  }

  // Экспорт в window для вызова из HTML / других немодульных скриптов
  window.clearModelsCache = () => {
    const sw = _getSW();
    if (!sw) { 
      alert('SW не активен'); 
      return; 
    }

    const handler = (e) => {
      if (e.data === 'MODELS_CACHE_CLEARED' || e.data?.type === 'MODELS_CACHE_CLEARED') {
        navigator.serviceWorker.removeEventListener('message', handler);
        alert('Кэш AI-моделей успешно очищен');
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    sw.postMessage('CLEAR_MODELS_CACHE');
  };

} // end initPWA
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Offline queue, cache monitor, scheduled updates
// ══════════════════════════════════════════════════════════════

let _updateCheckInterval = null;
const OFFLINE_QUEUE_KEY = 'mikhmel_offline_queue';   // старое хранилище, читаем для переноса

// ══════════════════════════════════════════════════════════════
// [10.0.3] Очередь отложенной отправки — в IndexedDB, а не в localStorage.
//
// Присланный вариант делал это через пакет idb; пакета в зависимостях нет, и
// сборка на нём падала. Здесь то же самое на голом IndexedDB — двадцать строк
// и ни одной новой зависимости.
//
// Почему вообще переезд с localStorage: он синхронный (каждая запись
// подвешивает поток отрисовки), ограничен пятью мегабайтами на весь домен —
// теми же, из которых расходуются настройки и аннотации, — и хранит только
// строки, то есть каждый разбор очереди это JSON.parse всего массива целиком.
// ══════════════════════════════════════════════════════════════
const DB_NAME = 'mikhmel-pwa';
const STORE = 'offline-queue';
let _dbPromise = null;

function _openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    let req;
    try { req = indexedDB.open(DB_NAME, 1); }
    catch (e) { reject(e); return; }
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = () => reject(req.error);
  }).catch((e) => { _dbPromise = null; throw e; });
  return _dbPromise;
}

function _tx(mode, fn) {
  return _openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let out;
    try { out = fn(store); } catch (e) { reject(e); return; }
    tx.oncomplete = () => resolve(out && typeof out.result !== 'undefined' ? out.result : out);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }));
}

/** Разовый перенос старой очереди из localStorage. */
async function _migrateLegacyQueue() {
  let old = [];
  try { old = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]'); } catch { return; }
  if (!Array.isArray(old) || !old.length) return;
  try {
    await _tx('readwrite', (store) => { for (const item of old) store.put(item); });
    localStorage.removeItem(OFFLINE_QUEUE_KEY);
    log.info(`[PWA] очередь перенесена в IndexedDB: ${old.length} записей`);
  } catch (e) { log.warn('[PWA] перенос очереди не удался:', e.message); }
}

/**
 * Отложить отправку до появления связи.
 * @param {string} type — 'annotations' | 'report' | 'measurement'
 * @param {Object} data
 */
export async function queueForSync(type, data) {
  const item = { id: `${type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, type, data, timestamp: Date.now() };
  try {
    await _tx('readwrite', (store) => store.put(item));
    log.debug(`[PWA] отложено: ${type}`);
  } catch (e) {
    log.warn('[PWA] очередь недоступна, пишу в localStorage:', e.message);
    try {
      const q = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
      q.push(item);
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(q));
    } catch { /* совсем некуда — теряем, но не роняем */ }
  }
  // Фоновая синхронизация, если браузер умеет.
  try {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync?.register(`sync-${type}`);
    }
  } catch (e) { log.debug('[PWA] background sync:', e.message); }
}

/** Что лежит в очереди. */
export async function getOfflineQueue() {
  try { return await _tx('readonly', (store) => store.getAll()) || []; }
  catch (e) { log.debug('[PWA] чтение очереди:', e.message); return []; }
}

/**
 * Отправить накопленное.
 *
 * [10.0.3] sender обязателен, и его отсутствие больше не роняет программу.
 * В присланном варианте обработчик события «связь появилась» звал
 * processOfflineQueue() БЕЗ аргумента, а внутри стояло `await sender(item)` —
 * TypeError ровно в момент возврата связи, то есть тогда, когда всё как раз
 * должно было заработать.
 *
 * @param {(item: object) => Promise<boolean>} [sender]
 * @returns {Promise<{sent:number, failed:number, reason?:string}>}
 */
export async function processOfflineQueue(sender) {
  if (!navigator.onLine) return { sent: 0, failed: 0, reason: 'offline' };
  if (typeof sender !== 'function') {
    const pending = (await getOfflineQueue()).length;
    if (pending) log.debug(`[PWA] в очереди ${pending}, но отправлять некому — sender не задан`);
    return { sent: 0, failed: 0, reason: 'no-sender' };
  }

  const items = await getOfflineQueue();
  let sent = 0, failed = 0;
  for (const item of items) {
    try {
      if (await sender(item)) {
        await _tx('readwrite', (store) => store.delete(item.id));
        sent++;
      } else failed++;
    } catch (e) { failed++; }
  }
  return { sent, failed };
}

/** Очистить очередь. */
export async function clearOfflineQueue() {
  try { await _tx('readwrite', (store) => store.clear()); }
  catch (e) { log.debug('[PWA] очистка очереди:', e.message); }
  try { localStorage.removeItem(OFFLINE_QUEUE_KEY); } catch { /* */ }
}

/**
 * [10.0.3] Место на диске: если занято больше 90% квоты, просим служебный
 * поток сбросить самый тяжёлый кэш — модели ИИ. Иначе браузер однажды снесёт
 * всё хранилище целиком, вместе с сохранёнными исследованиями.
 */
export async function checkStorageQuota() {
  const stats = await getCacheStorageStats();
  if (!stats || !(stats.percent > 90)) return stats;
  log.warn(`[PWA] хранилище занято на ${stats.percent}% — сбрасываю кэш моделей`);
  try { _getSW()?.postMessage('CLEAR_MODELS_CACHE'); } catch (e) { /* */ }
  return stats;
}

/**
 * Start auto-checking for SW updates every N minutes
 * @param {number} [intervalMin=30]
 */
export function startAutoUpdateCheck(intervalMin = 30) {
  stopAutoUpdateCheck();

  // [10.0.3] Периодическая фоновая синхронизация, если браузер её умеет:
  // проверка обновлений тогда работает и с закрытой вкладкой.
  //
  // В присланном коде условие было написано как `'periodicSync' in
  // Registration.prototype` — глобального `Registration` в браузере не
  // существует вовсе, и строка падала ReferenceError'ом, забирая с собой всю
  // функцию: автопроверка не включалась никогда. Правильное имя —
  // ServiceWorkerRegistration.
  try {
    if ('serviceWorker' in navigator
      && typeof ServiceWorkerRegistration !== 'undefined'
      && 'periodicSync' in ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.periodicSync?.register('check-updates', { minInterval: intervalMin * 60 * 1000 })
          .catch(() => { _startIntervalFallback(intervalMin); });
      }).catch(() => _startIntervalFallback(intervalMin));
      log.debug(`[PWA] periodic sync: проверка обновлений каждые ${intervalMin} мин`);
      return;
    }
  } catch (e) { log.debug('[PWA] periodicSync:', e.message); }

  _startIntervalFallback(intervalMin);
}

function _startIntervalFallback(intervalMin) {
  if (_updateCheckInterval) return;
  _updateCheckInterval = setInterval(async () => {
    if (_swRegistration) {
      try { await _swRegistration.update(); log.debug('[PWA] проверка обновления'); }
      catch (e) { log.debug('[pwaInstall]', e.message); }
    }
  }, intervalMin * 60000);
  log.debug(`[PWA] проверка обновлений каждые ${intervalMin} мин`);
}

/**
 * Stop auto-update checking
 */
export function stopAutoUpdateCheck() {
  if (_updateCheckInterval) { clearInterval(_updateCheckInterval); _updateCheckInterval = null; }
}

/**
 * Get detailed cache storage usage
 * @returns {Promise<{usageMB:number, quotaMB:number, percent:number}>}
 */
export async function getCacheStorageStats() {
  try {
    if (!navigator.storage?.estimate) return null;
    const est = await navigator.storage.estimate();
    return {
      usageMB: +(est.usage / 1048576).toFixed(1),
      quotaMB: +(est.quota / 1048576).toFixed(0),
      percent: +((est.usage / est.quota) * 100).toFixed(1),
    };
  } catch (e) { log.warn(`[pwaInstall] getCacheStorageStats failed:`, e.message); return {}; }
}

/** Basic state */
export function getPWAState() {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || !!window.navigator.standalone;
  return {
    installed: isStandalone, online: navigator.onLine,
    sw: _swRegistration ? 'registered' : 'none',
    // Длина очереди теперь асинхронна (IndexedDB) — спрашивайте getOfflineQueue().
    offlineQueue: null,
    autoUpdate: !!_updateCheckInterval,
  };
}
export function isPWAInstalled() { return window.matchMedia('(display-mode: standalone)').matches || !!window.navigator.standalone; }
export async function checkForUpdate() { if (_swRegistration) { await _swRegistration.update(); return true; } return false; }
export function getServiceWorkerInfo() { if (!_swRegistration) return null; return { scope: _swRegistration.scope, active: !!_swRegistration.active }; }
export function requestCacheSize() { const sw = _getSW(); if (sw) sw.postMessage('GET_CACHE_SIZE'); }
export function clearModelsCache() { if (typeof window.clearModelsCache === 'function') window.clearModelsCache(); }
